import {
  ChevronRight,
  CircleAlert,
  Coffee,
  Droplets,
  type LucideIcon,
  Smile,
  Utensils,
  Zap,
} from "lucide-react"

import { Badge } from "@/components/ui/badge"

const entryTypeConfig: Record<
  string,
  { icon: LucideIcon; bgVar: string; iconVar: string }
> = {
  symptom: {
    icon: CircleAlert,
    bgVar: "--fig-entry-symptom-bg",
    iconVar: "--fig-entry-symptom-icon",
  },
  meal: {
    icon: Utensils,
    bgVar: "--fig-entry-meal-bg",
    iconVar: "--fig-entry-meal-icon",
  },
  mood: {
    icon: Smile,
    bgVar: "--fig-entry-mood-bg",
    iconVar: "--fig-entry-mood-icon",
  },
  energy: {
    icon: Zap,
    bgVar: "--fig-entry-mood-bg",
    iconVar: "--fig-entry-mood-icon",
  },
  beverage: {
    icon: Coffee,
    bgVar: "--fig-entry-beverage-bg",
    iconVar: "--fig-entry-beverage-icon",
  },
  drink: {
    icon: Coffee,
    bgVar: "--fig-entry-beverage-bg",
    iconVar: "--fig-entry-beverage-icon",
  },
  water: {
    icon: Droplets,
    bgVar: "--fig-entry-water-bg",
    iconVar: "--fig-entry-water-icon",
  },
  stool: {
    icon: Droplets,
    bgVar: "--fig-entry-stool-bg",
    iconVar: "--fig-entry-stool-icon",
  },
}

export interface TimelineEntry {
  id: string
  type: keyof typeof entryTypeConfig
  title: string
  description?: string
  severity?: string
  time: string
  tags?: { id: number; name: string }[]
}

export interface TimeGroup {
  label: string
  entries: TimelineEntry[]
}

interface EntryTimelineProps {
  groups: TimeGroup[]
  onEntryClick?: (entryId: string) => void
}

export function EntryTimeline({ groups, onEntryClick }: EntryTimelineProps) {
  return (
    <div className="flex flex-col gap-3">
      {groups.map((group) => (
        <div
          key={group.label}
          className="border-fig-card-border bg-fig-card-bg shadow-fig-card overflow-hidden rounded-2xl border"
        >
          <div className="px-4 pt-3 pb-1">
            <span className="text-fig-text-placeholder text-[11px] font-semibold tracking-[0.05em] uppercase">
              {group.label}
            </span>
          </div>

          {group.entries.map((entry, idx) => {
            const config = entryTypeConfig[entry.type]
            if (!config) return null
            const Icon = config.icon
            const isLast = idx === group.entries.length - 1

            return (
              <div
                key={entry.id}
                className={`timeline-entry flex cursor-pointer items-center gap-3 px-4 py-3 ${
                  !isLast ? "border-fig-card-border border-b" : ""
                }`}
                onClick={() => onEntryClick?.(entry.id)}
              >
                <div
                  className="flex size-9 shrink-0 items-center justify-center rounded-[10px]"
                  style={{ background: `var(${config.bgVar})` }}
                >
                  <Icon size={20} style={{ color: `var(${config.iconVar})` }} />
                </div>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <span className="text-[14px] font-semibold">
                      {entry.title}
                    </span>
                    {entry.severity && (
                      <span className="text-fig-text-placeholder text-[11px]">
                        {entry.severity}
                      </span>
                    )}
                  </div>
                  {entry.description && (
                    <p className="text-fig-text-inactive mt-px truncate text-[13px]">
                      {entry.description}
                    </p>
                  )}
                  {entry.tags && entry.tags.length > 0 && (
                    <div className="mt-1 flex flex-wrap gap-1">
                      {entry.tags.map((tag) => (
                        <Badge
                          key={tag.id}
                          variant="secondary"
                          className="border-none text-[11px]"
                          style={{
                            background: "var(--fig-tag-bg)",
                            color: "var(--fig-tag-text)",
                          }}
                        >
                          {tag.name}
                        </Badge>
                      ))}
                    </div>
                  )}
                </div>
                <span className="text-fig-text-placeholder mr-0.5 shrink-0 text-[12px]">
                  {entry.time}
                </span>
                <ChevronRight
                  size={16}
                  className="text-fig-text-placeholder shrink-0"
                />
              </div>
            )
          })}
        </div>
      ))}
    </div>
  )
}
